import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useFilterStore } from '../../stores/filter-store';
import FilterModal from './FilterModal';

interface SearchBarProps {
  onSearch?: (query: string) => void;
  context?: 'dashboard' | 'fulfillment' | 'operation';
  onApplyFilters?: (filters: any) => void;
  onClearFilters?: () => void;
}

function SearchBar({ onSearch, context = 'dashboard', onApplyFilters, onClearFilters }: SearchBarProps) {
  const { 
    filters, 
    fulfillmentFilters, 
    setSearchQuery, 
    setFulfillmentSearchQuery,
    clearFulfillmentFilters,
    clearAllFilters
  } = useFilterStore();
  
  // For operation context, we don't use the filter store directly
  // The operation page uses its own operation filter store
  const currentSearchQuery = context === 'fulfillment' ? fulfillmentFilters.searchQuery : 
                           context === 'operation' ? '' : filters.searchQuery;
  const [query, setQuery] = useState(currentSearchQuery);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const isFulfillmentContext = context === 'fulfillment';

  const formClass = isFulfillmentContext ? 'flex items-center gap-2.5' : 'flex gap-3 items-center';
  const searchWidthClass = isFulfillmentContext ? 'relative w-[240px]' : 'relative w-56';
  const inputClass = isFulfillmentContext
    ? 'w-full rounded-full border border-[#DDE3F1] bg-[#F9FAFC] py-2 pl-10 pr-4 text-[13px] font-medium text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:border-brand-secondary'
    : 'pl-10 pr-4 py-2.5 rounded-full border border-gray-300 bg-gray-100 font-medium text-gray-900 placeholder-gray-500 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:border-brand-secondary';
  const controlsClass = isFulfillmentContext ? 'flex items-center gap-2.5' : 'flex gap-3 items-center';
  const filterBtnClass = isFulfillmentContext
    ? 'flex items-center gap-2 rounded-full bg-[#FFF5CC] px-3.5 py-2 text-[13px] font-medium text-[#6B7280] transition hover:bg-[#FFECA8]'
    : 'px-4 py-2.5 rounded-lg bg-[#FFF9E0] text-gray-700 font-medium text-sm flex items-center gap-2 hover:bg-yellow-200 transition';
  const refreshBtnClass = isFulfillmentContext
    ? 'rounded-lg bg-[#EFF1F5] p-2 text-gray-600 transition hover:bg-[#E4E8EF]'
    : 'p-2.5 rounded-md bg-gray-200 text-gray-600 hover:bg-gray-300 transition';

  // Sync with store
  useEffect(() => {
    setQuery(currentSearchQuery);
  }, [currentSearchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (context === 'fulfillment') {
      setFulfillmentSearchQuery(query);
    } else if (context === 'operation') {
      // For operation context, just call onSearch - the operation filter store is handled by FilterToolbar
      onSearch?.(query);
    } else {
      setSearchQuery(query);
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    
    // Update store immediately for real-time filtering
    if (context === 'fulfillment') {
      setFulfillmentSearchQuery(newQuery);
    } else if (context === 'operation') {
      // For operation context, call onSearch immediately for real-time filtering
      onSearch?.(newQuery);
    } else {
      setSearchQuery(newQuery);
    }
  };

  // const handleApplyFilters = (filters: any) => {
  //   if (context === 'fulfillment') {
  //     // The filters are already applied to the store by the FilterModal
  //     // We just need to trigger a refresh of the orders
  //     console.log('Applied filters from modal:', filters);
  //   }
  // };

  const handleClearFilters = () => {
    // Clear search query
    setQuery('');
    
    // Clear filters based on context
    if (context === 'fulfillment') {
      clearFulfillmentFilters();
      setFulfillmentSearchQuery('');
    } else if (context === 'operation') {
      // For operation context, just call onClearFilters - the operation filter store is handled by FilterToolbar
      onClearFilters?.();
    } else {
      clearAllFilters();
      setSearchQuery('');
    }
    
    // Call the onClearFilters callback if provided
    onClearFilters?.();
    
    console.log('Cleared all filters and search');
  };

  const openFilterModal = () => {
    console.log('Filter button clicked - opening modal');
    setIsFilterModalOpen(true);
  };
  
  const closeFilterModal = () => setIsFilterModalOpen(false);

  return (
    <>
      <form onSubmit={handleSubmit} className={formClass}>
        <div className={searchWidthClass}>
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={16} />
          </span>
          <input
            type="text"
            placeholder="Search for Orders"
            value={query}
            onChange={handleQueryChange}
            className={inputClass}
          />
        </div>
        <div className={controlsClass}>
          <button 
            type="button" 
            onClick={openFilterModal}
            className={filterBtnClass}
          >
            <img 
              src="/img/filter.svg" 
              alt="Filter" 
              className="h-4 w-4"
            />
            Filter
          </button>
          <button 
            type="button" 
            onClick={handleClearFilters}
            className={refreshBtnClass}
            title="Clear all filters and search"
          >
            <img 
              src="/img/refresh.svg" 
              alt="Refresh" 
              className="w-4 h-4"
            />
          </button>
        </div>
      </form>

      {/* Filter Modal */}
      <FilterModal 
        isOpen={isFilterModalOpen} 
        onClose={closeFilterModal}
        context={context}
        onApplyFilters={onApplyFilters}
      />
    </>
  );
}

export default SearchBar; 